import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  Typography,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";

import StepAccount from "./Register/StepAccount";
import StepProfile from "./Register/StepProfile";

import {
  completeCollaboratorRegister,
  organisationAPI,
  login,
} from "api/Accounts/AccountsAPI";

import { Organisation } from "api/Accounts/AccountsInterfaces";
import { backendOrigin } from "api/APIClient";

interface AccountRegistrationData {
  username?: string;
  email?: string;
  password?: string;
}

interface ProfileRegistrationData {
  first_name?: string;
  last_name?: string;
  phone_number?: string;
  department?: string;
  position_title?: string;
  role?: number | string | null;
  permissions?: Array<number | string>;
}

type RegistrationError = Record<string, any> | null;

export default function RegisterCollaborator() {
  const { publicToken } = useParams<{
    publicToken: string;
  }>();

  const navigate = useNavigate();

  const [step, setStep] = useState(1);

  const [accountData, setAccountData] =
    useState<AccountRegistrationData>({});

  const [profileData, setProfileData] =
    useState<ProfileRegistrationData>({});

  const [organisation, setOrganisation] =
    useState<Organisation | null>(null);

  const [error, setError] =
    useState<RegistrationError>(null);

  const [organisationLoading, setOrganisationLoading] =
    useState(true);

  const [submitting, setSubmitting] =
    useState(false);

  useEffect(() => {
    async function loadOrganisation() {
      if (!publicToken) {
        setError({
          _form:
            "This registration link does not contain an organisation token.",
        });

        setOrganisationLoading(false);
        return;
      }

      if (publicToken.length !== 10) {
        setError({
          _form: "This organisation registration link is invalid.",
        });

        setOrganisationLoading(false);
        return;
      }

      try {
        setOrganisationLoading(true);
        setError(null);

        const organisationResponse =
          await organisationAPI.getByToken(publicToken);

        setOrganisation(organisationResponse);
      } catch (err: any) {
        console.error(
          "Failed to load organisation:",
          err
        );

        setError({
          _form:
            err.response?.data?.detail ||
            err.response?.data?.public_token ||
            "The organisation could not be found. The registration link may be invalid or expired.",
        });
      } finally {
        setOrganisationLoading(false);
      }
    }

    void loadOrganisation();
  }, [publicToken]);

  const organisationLogo = organisation?.logo;

  const organisationLogoUrl = useMemo(() => {
    if (typeof organisationLogo === "string") {
      const trimmedLogo = organisationLogo.trim();

      if (!trimmedLogo) {
        return null;
      }

      return /^https?:\/\//i.test(trimmedLogo)
        ? trimmedLogo
        : `${backendOrigin}${trimmedLogo}`;
    }

    if (organisationLogo instanceof File) {
      return URL.createObjectURL(organisationLogo);
    }

    return null;
  }, [organisationLogo]);

  useEffect(() => {
    return () => {
      if (
        organisationLogo instanceof File &&
        organisationLogoUrl
      ) {
        URL.revokeObjectURL(organisationLogoUrl);
      }
    };
  }, [organisationLogo, organisationLogoUrl]);

  async function handleFinalSubmit(
    account: AccountRegistrationData,
    profile: ProfileRegistrationData
  ) {
    if (!publicToken || !organisation) {
      setError({
        _form: "The organisation could not be identified.",
      });

      return;
    }

    if (
      !account.username ||
      !account.password ||
      !profile.first_name ||
      !profile.last_name
    ) {
      setError({
        _form:
          "Some required registration information is missing.",
      });

      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      await completeCollaboratorRegister(publicToken, {
        account: {
          username: account.username,
          email: account.email,
          password: account.password,
        },

        profile: {
          first_name: profile.first_name,
          last_name: profile.last_name,
          phone_number: profile.phone_number,
          department: profile.department,
          position_title: profile.position_title,

          role:
            typeof profile.role === "number"
              ? profile.role
              : undefined,

          permissions: profile.permissions
            ?.map((permission) => Number(permission))
            .filter((permission) =>
              Number.isInteger(permission)
            ),
        },
      });

      await login(
        account.username,
        account.password
      );

      navigate("/accounts/organisation/", {
        replace: true,
      });
    } catch (err: any) {
      console.error(
        "Collaborator registration failed:",
        err
      );

      const backendError =
        err.response?.data || {
          _form:
            err.message ||
            "Registration failed. Please check your details and try again.",
        };

      setError(backendError);

      if (
        backendError.username ||
        backendError.email ||
        backendError.account_email ||
        backendError.password ||
        backendError.account
      ) {
        setStep(1);
        return;
      }

      setStep(2);
    } finally {
      setSubmitting(false);
    }
  }

  if (organisationLoading) {
    return (
      <Box
        sx={{
          minHeight: 300,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!organisation) {
    return (
      <Box
        sx={{
          maxWidth: 600,
          mx: "auto",
          mt: 4,
          px: 2,
        }}
      >
        <Alert severity="error">
          {error?._form ||
            "The organisation could not be loaded."}
        </Alert>
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          maxWidth: 700,
          mx: "auto",
          px: 2,
          pt: 3,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: {
              xs: 2,
              sm: 3,
            },
          }}
        >
          {organisationLogoUrl && (
            <Box
              component="img"
              src={organisationLogoUrl}
              alt={`${organisation.name} logo`}
              sx={{
                display: "block",
                flexShrink: 0,
                width: "auto",
                height: {
                  xs: 55,
                  sm: 75,
                },
                maxWidth: {
                  xs: 120,
                  sm: 180,
                },
                objectFit: "contain",
              }}
            />
          )}

          <Typography
            variant="h4"
            component="h1"
            fontWeight={700}
            sx={{
              textAlign: "left",
              lineHeight: 1.2,
            }}
          >
            Join {organisation.name} on Pathways
          </Typography>
        </Box>


        {error?._form && (
          <Alert
            severity="error"
            sx={{
              mb: 2,
              textAlign: "left",
            }}
          >
            {error._form}
          </Alert>
        )}

        {submitting && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mb: 2,
            }}
          >
            <CircularProgress size={28} />
          </Box>
        )}
      </Box>

      {step === 1 && (
        <StepAccount
          data={accountData}
          error={error}
          onNext={(data: AccountRegistrationData) => {
            if (submitting) {
              return;
            }

            setError(null);
            setAccountData(data);
            setStep(2);
          }}
        />
      )}

      {step === 2 && (
        <StepProfile
          data={profileData}
          error={error}
          onNext={(data: ProfileRegistrationData) => {
            if (submitting) {
              return;
            }

            setProfileData(data);

            void handleFinalSubmit(
              accountData,
              data
            );
          }}
          onBack={() => {
            if (submitting) {
              return;
            }

            setError(null);
            setStep(1);
          }}
        />
      )}
    </>
  );
}
